import { useQueryClient } from '@tanstack/react-query';
import type { PropsWithChildren } from 'react';
import { createContext, useContext, useRef, useState } from 'react';
import { useAxiosInstance } from '@gov-nx/api/axios';
import type { SecurityZahajitAutorizaciOdpovedDTO } from '@gov-nx/api/generated/security.types';
import { usePoMutation } from '@gov-nx/api/hooks/usePoMutation';
import { dokonciAutorizaci } from '@gov-nx/api/queries/authQuery';
import { form602SubmissionDeleteQuery } from '@gov-nx/api/queries/formsQuery';
import { useAuthEvents } from '@gov-nx/core/events/Auth/useAuthEvents';
import { useMessageEvents } from '@gov-nx/core/events/Message/useMessageEvents';
import { useLocale } from '@gov-nx/core/service/translations/useLocale';
import type { Nullable, Optional } from '@gov-nx/core/types/helpers.types';
import {
	is,
	isCallable,
	isNonEmptyString,
} from '@gov-nx/core/types/helpers.types';
import { PageCode } from '@gov-nx/module/page/definitions/codes';
import { isHighAuthorization } from '@gov-nx/store/autorizace/authorizationLevel';
import { useAutorizaceStore } from '@gov-nx/store/autorizace/store';
import type { ProcessSuccessToast } from '@gov-nx/store/autorizace/store.types';
import { useBoolean } from '@gov-nx/utils/common/Hooks/useBoolean';
import type { AutorizaceContextType } from './context.types';
import { useAuthorizationReset } from './hooks/useAuthorizationReset';
import { useConfirmAuthCode } from './hooks/useConfirmAuthCode';
import { useSendAuthCodeQuery } from './hooks/useSendAuthCodeQuery';
import { useZahajAutorizaciQuery } from './hooks/useZahajAutorizaciQuery';
import type { AuthorizationType } from './types';
import { useProcessSuccessMessage } from './useProcessSuccessMessage';

const AutorizaceContext = createContext<Nullable<AutorizaceContextType>>(null);

export interface AuthorizationReturnUrl {
	save: () => void;
	clear: () => void;
}

interface AutorizaceContextProviderProps extends PropsWithChildren {
	onCodeAuthorizationFailed: (formularPodaniId: string) => void;
	returnUrl?: AuthorizationReturnUrl;
}

export function AutorizaceContextProvider({
	children,
	onCodeAuthorizationFailed,
	returnUrl,
}: AutorizaceContextProviderProps) {
	const code = PageCode['autorizace'];
	const { axiosInstance } = useAxiosInstance();
	const queryClient = useQueryClient();
	const { toastMessageSuccess, toastMessageError } = useMessageEvents();
	const { t } = useLocale(code);
	const authorizationDialog = useBoolean();
	const ukonId = useAutorizaceStore('ukonId');
	const setUkonId = useAutorizaceStore('setUkonId');
	// polozkaAutorizaceId used to start the current authorization; kept so the
	// temporary submission can be cleaned up if the user closes the dialog.
	const [activePolozkaAutorizaceId, setActivePolozkaAutorizaceId] =
		useState<string>('');
	const getServiceAuthorizationType = useAutorizaceStore(
		'getServiceAuthorizationType'
	);
	const getAuthorizationId = useAutorizaceStore('getAuthorizationId');
	const setAuthorizationId = useAutorizaceStore('setAuthorizationId');
	const clearAuthorization = useAutorizaceStore('clearAuthorization');
	const setIsFailureSheetVisible = useAutorizaceStore(
		'setIsFailureSheetVisible'
	);
	const setFailedSubmissionId = useAutorizaceStore('setFailedSubmissionId');
	const setProcessSuccessToast = useAutorizaceStore('setProcessSuccessToast');
	const setLaunchSiteHandlesCancel = useAutorizaceStore(
		'setLaunchSiteHandlesCancel'
	);
	const resolveProcessSuccessMessage = useProcessSuccessMessage();
	const confirmPendingAuthorizedChange = useAutorizaceStore(
		'confirmPendingAuthorizedChange'
	);
	const clearPendingAuthorizedChange = useAutorizaceStore(
		'clearPendingAuthorizedChange'
	);
	const { reset } = useAuthorizationReset({
		onResetFlow: () => {
			attemptRef.current += 1;
			onDialogDismissRef.current = undefined;
			onAuthorizationFlowRef.current = undefined;
			flowRaisesSuccessToastRef.current = false;
			returnUrl?.clear();
			setActivePolozkaAutorizaceId('');
			authorizationDialog.setFalse();
		},
	});
	useAuthEvents({ onUserLogout: reset });

	const cleanupAuthorization = usePoMutation({
		mutationFn: form602SubmissionDeleteQuery,
		onSuccess: (_data, variables) => {
			// The temporary submission was removed on the BE. Drop its cached detail
			// (and the pending list) so a detail view still open on it reloads to the
			// "no longer exists" state — no full window reload, no stale data. If the
			// detail isn't mounted (the usual case), this is a cheap no-op.
			queryClient.invalidateQueries({
				queryKey: ['form602-submission', variables.formularPodaniId],
			});
			queryClient.invalidateQueries({ queryKey: ['form602-submissions'] });
		},
	});

	const finalizeAuthorization = usePoMutation({
		mutationFn: dokonciAutorizaci,
	});

	// Guards against a late `startAuthorization` result writing the ids / return URL
	// back into storage after the dialog was already closed/cancelled — which would
	// resurrect a cancelled authorization and leak stale ids into the next flow.
	const attemptRef = useRef(0);

	// Dismiss handler of the flow currently in the dialog, if its launch site
	// passed one — see `onInitAuthorization`.
	const onDialogDismissRef = useRef<Optional<() => void>>(undefined);

	// Launch site's post-submit work, held until the authorization succeeds.
	const onAuthorizationFlowRef = useRef<Optional<() => void>>(undefined);

	// A declared `successToast` means that flow raises the message itself.
	const flowRaisesSuccessToastRef = useRef(false);

	const startAuthorization = useZahajAutorizaciQuery();

	const applyStartResult = (
		data: SecurityZahajitAutorizaciOdpovedDTO,
		attempt: number,
		handlesOwnDismiss: boolean
	) => {
		if (attempt !== attemptRef.current) {
			// The dialog was already closed/cancelled before this start settled.
			// We must NOT resurrect any local state (the race this guard exists
			// for), but the backend did create an authorization — close it with
			// `dokoncit` so it doesn't leak open. onDialogCloseClick couldn't have
			// finalized it: no autorizaceId was stored yet at close time.
			// Flows handling their own dismiss opted out of that background call.
			if (data.autorizaceId && !handlesOwnDismiss) {
				finalizeAuthorization.mutate({
					axiosInstance,
					authorizationId: data.autorizaceId,
				});
			}
			return;
		}
		if (is(data.autorizaceId)) {
			setAuthorizationId(data.autorizaceId);
		}
		setUkonId(data.ukonId);
		returnUrl?.save();
	};

	/**
	 * Completion point of a successful authorization for the legs that stay in
	 * the app: the in-dialog code flow below, and the mobile redirect callback
	 * (which reaches it through `onAuthorizationFinished`). The eID leg on web
	 * comes back into a freshly started app and confirms the change from its own
	 * return route instead.
	 *
	 * Confirming the change is what releases the view waiting to read it back —
	 * it may be mounted, or it may only mount once the user returns to it.
	 *
	 * Runs the launch site's flow too, and returns whether it raised the
	 * success message — see `successToast` in `onInitAuthorization`.
	 */
	const finishAuthorization = (): boolean => {
		confirmPendingAuthorizedChange();
		returnUrl?.clear();

		const onAuthorizationFlow = onAuthorizationFlowRef.current;
		const raisesSuccessToast = flowRaisesSuccessToastRef.current;
		onAuthorizationFlowRef.current = undefined;
		flowRaisesSuccessToastRef.current = false;
		if (!isCallable(onAuthorizationFlow)) {
			return false;
		}
		onAuthorizationFlow();
		return raisesSuccessToast;
	};

	const confirmAuthCode = useConfirmAuthCode({
		onSuccess: async ({ data }) => {
			if (data.status === 'SPATNY_KOD') {
				toastMessageError(t('dialog.messages.spatny-kod'));
				return;
			}

			if (data.status === 'NEAUTORIZOVANO') {
				toastMessageError(t('dialog.messages.neautorizovano'));
				return;
			}

			if (data.status !== 'OK') {
				toastMessageError();
				return;
			}

			const processMessage = await resolveProcessSuccessMessage();

			authorizationDialog.setFalse();
			clearAuthorization();
			// Stands in only where the flow did not raise the message itself.
			if (!finishAuthorization()) {
				toastMessageSuccess(processMessage ?? t('dialog.messages.success'));
			}
		},
		onError: () => {
			const failedSubmissionId = activePolozkaAutorizaceId;
			attemptRef.current += 1;
			onAuthorizationFlowRef.current = undefined;
			flowRaisesSuccessToastRef.current = false;
			clearAuthorization();
			returnUrl?.clear();
			setActivePolozkaAutorizaceId('');
			authorizationDialog.setFalse();
			onCodeAuthorizationFailed(failedSubmissionId);
		},
	});

	const sendAuthCodeQuery = useSendAuthCodeQuery();

	const onNextStep = (
		authorizationType: AuthorizationType.FCM | AuthorizationType.SMS
	) => {
		const authorizationId = getAuthorizationId();
		if (!is(authorizationId)) {
			return;
		}
		sendAuthCodeQuery.mutate({
			axiosInstance,
			authorizationId,
			body: {
				// TODO: proper fix when auth type is not correct
				kanal: authorizationType,
			},
		});
	};

	const onConfirmSubmit = (data: any) => {
		const authorizationId = getAuthorizationId();
		if (!is(authorizationId)) {
			return;
		}
		confirmAuthCode.mutate({
			axiosInstance,
			body: { kod: data.oneTimePassword },
			authorizationId,
		});
	};

	/**
	 * Single post-submit handoff for every service. The user is pushed into the
	 * authorization flow only when both hold:
	 *  1. the backend returned a `polozkaAutorizaceId` (`x-autorizaceid`), and
	 *  2. the service is configured for high (`VYSOKA`) authorization.
	 *
	 * Any other case — no id, a non-enforced service, or low (`NIZKA`)
	 * authorization — runs `onAuthorizationFlow` straight away. This is what
	 * keeps the runtime consistent with the `isAuthorizationEnforced` button
	 * labels: a stray `x-autorizaceid` on a service that isn't enforced cannot
	 * trigger the authorization dialog.
	 *
	 * `onAuthorizationFlow` is the launch site's post-submit work, deferred by
	 * high authorization rather than skipped. It is told which leg it is on
	 * because only navigation differs: high authorization leaves the user put.
	 *
	 * `successToast` is that flow's success message, for the eID leg on web
	 * that reloads the app and loses the flow. Declaring it also means the
	 * flow raises the message itself, so the other legs leave the toast to it.
	 *
	 * `onDialogDismiss` lets a launch site take over what a dismissed dialog
	 * means for it. Passing it replaces the background cleanup `onDialogCloseClick`
	 * runs by default — see there for when that default is the wrong thing, and
	 * opts the flow out of the same cleanup on a user cancel at NIA.
	 */
	const onInitAuthorization = (
		serviceName: string,
		polozkaAutorizaceId?: string | null,
		onAuthorizationFlow?: (isHighAuthorization: boolean) => void,
		successToast?: ProcessSuccessToast,
		onDialogDismiss?: () => void
	) => {
		const nonHighAuthCallback = () => {
			if (isCallable(onAuthorizationFlow)) {
				onAuthorizationFlow(false);
			}
		};

		if (!isNonEmptyString(polozkaAutorizaceId)) {
			nonHighAuthCallback();
			return;
		}

		if (!isHighAuthorization(getServiceAuthorizationType(serviceName))) {
			nonHighAuthCallback();
			return;
		}
		onInitAuthorizationHigh(
			polozkaAutorizaceId,
			isCallable(onAuthorizationFlow)
				? () => onAuthorizationFlow(true)
				: undefined,
			successToast,
			onDialogDismiss
		);
	};

	const onInitAuthorizationHigh = (
		polozkaAutorizaceId: string,
		onAuthorizationFlow?: () => void,
		successToast?: ProcessSuccessToast,
		onDialogDismiss?: () => void
	) => {
		if (!isNonEmptyString(polozkaAutorizaceId)) {
			return;
		}
		const attempt = ++attemptRef.current;
		// A change left armed by a flow the user walked away from would otherwise
		// be confirmed by this one. The view arms its own right after this returns.
		clearPendingAuthorizedChange();
		// These mutations outlive the dialog they belong to, so without a reset a
		// second authorization mounts on the previous flow's state.
		sendAuthCodeQuery.reset();
		confirmAuthCode.reset();
		startAuthorization.reset();
		setUkonId(undefined);
		setIsFailureSheetVisible(false);
		setFailedSubmissionId(undefined);
		setProcessSuccessToast(successToast);
		onDialogDismissRef.current = onDialogDismiss;
		onAuthorizationFlowRef.current = onAuthorizationFlow;
		flowRaisesSuccessToastRef.current = is(successToast);
		// The ref cannot survive the NIA redirect; the return handler reads this.
		setLaunchSiteHandlesCancel(isCallable(onDialogDismiss));
		setActivePolozkaAutorizaceId(polozkaAutorizaceId);
		authorizationDialog.setTrue();
		startAuthorization
			.mutateAsync({
				axiosInstance,
				polozkaAutorizaceId,
			})
			.then(({ data }) =>
				applyStartResult(data, attempt, isCallable(onDialogDismiss))
			)
			.catch(() => undefined);
	};

	/**
	 * Closing the dialog aborts the authorization the same way a user cancel from
	 * NIA does: finalize the authorization on the BE (`dokoncit`, on every
	 * outcome), clean up local data, and remove the temporary submission (the
	 * request behind the "Zrušit formulář" button), keyed by polozkaAutorizaceId.
	 * The user stays where they are.
	 *
	 * That cleanup is a convenience for flows that created the submission in the
	 * background: we made it for the user, so we remove it for them too. Where the
	 * submission is the user's own podání they navigated to, the launch site passes
	 * `onDialogDismiss` instead and the close stays a plain close.
	 */
	const onDialogCloseClick = () => {
		// From here on, ignore any still-in-flight startAuthorization result.
		attemptRef.current += 1;
		const onDialogDismiss = onDialogDismissRef.current;
		if (isCallable(onDialogDismiss)) {
			onDialogDismiss();
		} else {
			const authorizationId = getAuthorizationId();
			if (authorizationId) {
				finalizeAuthorization.mutate({ axiosInstance, authorizationId });
			}
			if (isNonEmptyString(activePolozkaAutorizaceId)) {
				cleanupAuthorization.mutate({
					axiosInstance,
					formularPodaniId: activePolozkaAutorizaceId,
				});
			}
		}
		onDialogDismissRef.current = undefined;
		onAuthorizationFlowRef.current = undefined;
		flowRaisesSuccessToastRef.current = false;
		clearAuthorization();
		returnUrl?.clear();
		setActivePolozkaAutorizaceId('');
		authorizationDialog.setFalse();
	};

	return (
		<AutorizaceContext.Provider
			value={{
				onConfirmSubmit,
				code,
				startAuthorization,
				confirmAuthCode,
				sendAuthCodeQuery,
				onInitAuthorization,
				ukonId: ukonId ?? '',
				onNextStep,
				onDialogCloseClick,
				closeAuthorizationDialog: authorizationDialog.setFalse,
				onAuthorizationFinished: finishAuthorization,
				authorizationDialogOpen: authorizationDialog.value,
			}}>
			{children}
		</AutorizaceContext.Provider>
	);
}

export const useAutorizaceContext = () => useContext(AutorizaceContext);
